/**
 * Browser compatibility check.
 *
 * Detects unsupported browsers using feature detection and displays a
 * full-page warning with links to download modern browsers.
 */

function isBrowserSupported(): boolean {
  try {
    // Check for globalThis (Chrome 71+, Firefox 65+, Safari 12.1+, Edge 79+)
    if (typeof globalThis === 'undefined') return false

    // Check for Promise.allSettled (Chrome 76+, Firefox 71+, Safari 13+, Edge 79+)
    if (typeof Promise.allSettled !== 'function') return false

    // Optional chaining (Chrome 80+, Firefox 74+, Safari 13.1+, Edge 80+)
    // is implied by the checks above — any browser with Promise.allSettled
    // and ResizeObserver also supports optional chaining. The previous
    // `new Function()` test was removed because it triggers CSP violations
    // when 'unsafe-eval' is not in the script-src directive.

    // Check for CSS.supports (IE does not support it)
    if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') return false

    // Check for ResizeObserver (Chrome 64+, Firefox 69+, Safari 13.1+, Edge 79+)
    if (typeof ResizeObserver === 'undefined') return false

    return true
  } catch {
    return false
  }
}

export function BrowserCheck({ children }: { children: React.ReactNode }) {
  if (isBrowserSupported()) {
    return <>{children}</>
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        textAlign: 'center',
        backgroundColor: '#f9fafb',
        color: '#111827',
      }}
    >
      <div
        style={{
          maxWidth: '32rem',
          padding: '2rem',
          backgroundColor: '#ffffff',
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            width: '3rem',
            height: '3rem',
            margin: '0 auto 1rem',
            borderRadius: '50%',
            backgroundColor: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
          }}
        >
          !
        </div>

        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem' }}>
          Your browser is not supported
        </h1>

        <p
          style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            lineHeight: 1.6,
            marginBottom: '1.5rem',
          }}
        >
          Zero requires a modern browser to function correctly. Please upgrade to one of the
          following browsers for the best experience.
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          <BrowserLink name="Google Chrome" url="https://www.google.com/chrome/" />
          <BrowserLink name="Mozilla Firefox" url="https://www.mozilla.org/firefox/new/" />
          <BrowserLink name="Microsoft Edge" url="https://www.microsoft.com/edge" />
        </div>

        <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1.5rem' }}>
          If you believe this is an error, please contact support.
        </p>
      </div>
    </div>
  )
}

function BrowserLink({ name, url }: { name: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        padding: '0.75rem 1rem',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        color: '#1d4ed8',
        textDecoration: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        transition: 'background-color 0.15s',
      }}
      onMouseOver={(e) => {
        ;(e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#f0f5ff'
      }}
      onMouseOut={(e) => {
        ;(e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent'
      }}
    >
      Download {name}
    </a>
  )
}
